import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { WorkerRow } from "./workerRow.component";
import { WorkerProfile } from "./workerProfile.component";
import { Text } from "components/Typography/text.styled";
import { FontFamily, FontSize } from "config/font";
import { CustomIcon } from "components/icon/customIcon.component";
import { Color } from "config/colors";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { useOrganizationalStructureStore } from "web-apps/company/stores/organizationalStructureStore/organizationalStructureStore";
import { Container1, Container2, Row } from "./organizationalStructure.styled";

export const UnassignedWorkers = () => {
  const { t } = useTranslation();

  const [showDetails, setShowDetails] = useState(false);
  const [clickedWorkerId, setClickedWorkerId] = useState<number | undefined>(
    undefined
  );
  const [showWorkerProfile, setShowWorkerProfile] = useState(false);
  const [companyState] = useCompanyStore();
  const [organizationalStructureState, organizationalStructureActions] =
    useOrganizationalStructureStore();
  const companyId: number | null = companyState.company?.id ?? null;
  const unassignedWorkers =
    organizationalStructureState?.unassignedWorkers ?? [];
  const loadingRef = useRef(false);
  const fetchedForCompanyRef = useRef<Set<number>>(new Set());

  const getUnassignedWorkersRef = useRef(
    organizationalStructureActions.getUnassignedWorkers
  );
  useEffect(() => {
    getUnassignedWorkersRef.current =
      organizationalStructureActions.getUnassignedWorkers;
  }, [organizationalStructureActions]);

  useEffect(() => {
    if (!companyId) return;

    const alreadyFetched = fetchedForCompanyRef.current.has(companyId);
    const hasData =
      Array.isArray(unassignedWorkers) && unassignedWorkers.length > 0;
    if (alreadyFetched || hasData || loadingRef.current) return;

    loadingRef.current = true;
    (async () => {
      try {
        await getUnassignedWorkersRef.current(companyId, 0, 50);
        fetchedForCompanyRef.current.add(companyId);
      } catch (e) {
        console.error("Error when getting unassigned workers", e);
      } finally {
        loadingRef.current = false;
      }
    })();
  }, [companyId, unassignedWorkers.length]);

  useEffect(() => {
    if (clickedWorkerId != null) setShowWorkerProfile(true);
  }, [clickedWorkerId]);

  return (
    <div style={{ marginBottom: 100, marginTop: 70 }}>
      <Row>
        <Container1>
          <Text
            fontFamily={FontFamily.MontserratSemiBold}
            style={{ width: 300, marginRight: 40 }}
          >
            {t("OrganizationalStructure.UnassignedWorkers")}
          </Text>

          <Text
            fontSize={FontSize.Small}
            style={{ marginTop: 2, marginLeft: 90 }}
          >
            {`${unassignedWorkers.length} ${t(
              "OrganizationalStructure.Workers"
            )}`}
          </Text>
        </Container1>

        <Container2>
          <CustomIcon
            name={showDetails ? "upward_arrow" : "downward_arrow"}
            size="20px"
            color={Color.BurntSienna}
            onClick={() => setShowDetails((v) => !v)}
          />
        </Container2>
      </Row>

      {showDetails && (
        <div>
          {unassignedWorkers.map((worker: any) => (
            <WorkerRow
              key={worker.workerId}
              worker={worker}
              setClickedWorker={setClickedWorkerId}
              unassigned
            />
          ))}
        </div>
      )}

      {clickedWorkerId !== undefined && (
        <WorkerProfile
          worker={clickedWorkerId}
          showWorkerProfile={showWorkerProfile}
          setShowWorkerProfile={setShowWorkerProfile}
          setClickedWorker={setClickedWorkerId}
        />
      )}
    </div>
  );
};
